import { useState } from "react";
import { Link } from "react-router-dom";
import { featuredProducts } from "@/mocks/products";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";

const badgeColors: Record<string, string> = {
  新品: "bg-[#8B5E52] text-white",
  熱銷: "bg-[#3d2b1f] text-[#f5e8d8]",
  Outlet: "bg-[#c9a98a] text-[#3d2b1f]",
  特價: "bg-[#b8957a] text-white",
};

export default function FeaturedProducts() {
  const [hovered, setHovered] = useState<number | null>(null);
  const headerAnim = useScrollAnimation();
  const cardsAnim = useStaggerAnimation(featuredProducts.length, { threshold: 0.05 });

  return (
    <section className="bg-[#faf8f5] py-20 md:py-28 overflow-hidden" data-product-shop>
      <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
        {/* Header */}
        <div
          ref={headerAnim.ref as React.RefObject<HTMLDivElement>}
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4 transition-all duration-700 ${headerAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div>
            <p className="text-[#8B5E52] text-[1rem] tracking-[0.25em] uppercase font-medium mb-3">Featured Items</p>
            <h2 className="text-[#3d2b1f] text-[2.125rem] md:text-[2.5rem] font-bold leading-[1.4]">本月主打推薦</h2>
            <p className="text-[#7a6455] text-[1.1875rem] mt-3 max-w-lg leading-[1.75]">修飾版型、實穿好搭，精選本月最值得入手的設計師品牌精品</p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 text-[#8B5E52] text-[1.0625rem] font-medium hover:gap-3 transition-all cursor-pointer whitespace-nowrap">
            查看全部商品 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Products */}
        <div
          ref={cardsAnim.ref as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5"
        >
          {featuredProducts.map((product, i) => (
            <div
              key={product.id}
              className={`group bg-white rounded-lg overflow-hidden cursor-pointer transition-all duration-700 ${cardsAnim.visibleItems[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
              onMouseEnter={() => setHovered(product.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="relative aspect-[5/7] overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className={`absolute top-3 left-3 px-2.5 py-1 rounded text-[0.875rem] font-semibold ${badgeColors[product.badge] || "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                  {product.badge}
                </span>
                <div className={`absolute inset-0 bg-[#3d2b1f]/55 flex flex-col items-center justify-center gap-3 transition-opacity duration-300 ${hovered === product.id ? "opacity-100" : "opacity-0"}`}>
                  <Link to={`/product/${product.id}`} className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#3d2b1f] rounded-full text-[0.9375rem] font-semibold hover:bg-[#f5e8d8] transition-colors whitespace-nowrap cursor-pointer">
                    查看商品詳情
                  </Link>
                  <a href="https://line.me" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 border-2 border-white text-white rounded-full text-[0.9375rem] font-medium hover:bg-white/15 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-line-fill"></i> 立即詢問
                  </a>
                </div>
              </div>
              <div className="p-4">
                <p className="text-[#7a6455] text-[0.875rem] mb-1">{product.category}</p>
                <Link to={`/product/${product.id}`}>
                  <h3 className="text-[#3d2b1f] text-[1.0625rem] font-semibold leading-[1.45] mb-1 hover:text-[#8B5E52] transition-colors cursor-pointer">{product.name}</h3>
                </Link>
                <p className="text-[#8B5E52] text-[0.9375rem] mb-3">{product.tagline}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[#8B5E52] text-[1.125rem] font-bold">NT${product.salePrice.toLocaleString()}</span>
                  <span className="text-[#a09080] text-[0.9375rem] line-through">NT${product.originalPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
